import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as qs from 'qs';
import history from '../../../../../history';
import { getWeeksFromData } from '../../getColumns';
import FairProductCard from '../FairProductCard/FairProductCard';
import ButtonWithSpinner from '../../../../Utils/ButtonWithSpinner';
import Spinner from '../../../../Utils/Spinner';
import { Input } from 'mdbreact';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50, 100];

class FairProductsList extends Component {
    constructor(props) {
        super(props);

        const queryParams = qs.parse(history.location.search.slice(1));
        const page = parseInt(queryParams.page, 10) || 1;
        const limitFromUrl = parseInt(queryParams.limit, 10);
        const initialPageSize =
            props.pageSize ||
            (!Number.isNaN(limitFromUrl) && limitFromUrl > 0
                ? limitFromUrl
                : 50);

        this.state = {
            page,
            pageSize: initialPageSize,
            activeWeekDate: null,
        };
        this.listRef = React.createRef();
    }

    componentDidMount() {
        this.fetchPage(this.state.page, this.state.pageSize);
    }

    componentDidUpdate(prevProps) {
        const queryParams = qs.parse(history.location.search.slice(1));
        const pageFromUrl = parseInt(queryParams.page, 10) || 1;
        const limitFromUrl = parseInt(queryParams.limit, 10);

        const nextPageSize =
            !Number.isNaN(limitFromUrl) && limitFromUrl > 0
                ? limitFromUrl
                : this.state.pageSize;

        // Keep internal state aligned with URL (page + pageSize).
        // If we changed state, let the next lifecycle run before recalculating totals.
        if (pageFromUrl !== this.state.page || nextPageSize !== this.state.pageSize) {
            this.setState({
                page: pageFromUrl,
                pageSize: nextPageSize,
            });
            return;
        }

        if (prevProps.itemsCount !== this.props.itemsCount) {
            const totalPages = this.getTotalPages();
            if (this.state.page > totalPages) {
                this.goToPage(Math.max(totalPages, 1));
            }
        }
    }

    getTotalPages = () => {
        const { itemsCount } = this.props;
        const { pageSize } = this.state;
        if (!itemsCount) {
            return 1;
        }
        return Math.max(Math.ceil(itemsCount / pageSize), 1);
    };

    fetchPage = (page, pageSize) => {
        const queryParams = qs.parse(history.location.search.slice(1));
        const params = {
            ...queryParams,
            page: String(page),
            limit: pageSize,
        };

        history.push({
            ...history.location,
            search: qs.stringify(params, {
                arrayFormat: 'brackets',
            }),
        });

        this.props.onFetchData(params);
    };

    goToPage = (page) => {
        const totalPages = this.getTotalPages();
        const nextPage = Math.min(Math.max(page, 1), totalPages);

        this.setState({ page: nextPage }, () => {
            this.fetchPage(nextPage, this.state.pageSize);
            if (this.listRef.current) {
                this.listRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        });
    };

    handlePageInput = (event) => {
        const value = parseInt(event.target.value, 10);
        if (!value) {
            return;
        }
        this.goToPage(value);
    };

    handlePageSizeChange = (event) => {
        const pageSize = parseInt(event.target.value, 10);
        this.setState({ pageSize, page: 1 }, () => {
            this.fetchPage(1, pageSize);
        });
    };

    handleWeekFocus = (weekDate) => {
        if (this.weekBlurTimeout) {
            clearTimeout(this.weekBlurTimeout);
            this.weekBlurTimeout = null;
        }
        this.setState({ activeWeekDate: weekDate });
    };

    handleWeekBlur = () => {
        this.weekBlurTimeout = setTimeout(() => {
            this.setState({ activeWeekDate: null });
            this.weekBlurTimeout = null;
        }, 0);
    };

    componentWillUnmount() {
        if (this.weekBlurTimeout) {
            clearTimeout(this.weekBlurTimeout);
        }
    }

    renderPagination = () => {
        const { intl } = this.props;
        const { page, pageSize } = this.state;
        const totalPages = this.getTotalPages();

        return (
            <div className="pagination-bottom fair-products-list__pagination">
                <div className="-pagination">
                    <div className="-previous">
                        <button
                            type="button"
                            className="-btn"
                            disabled={page <= 1}
                            onClick={() => this.goToPage(page - 1)}
                        >
                            {intl.formatMessage({
                                id: 'table.footer.prev_text',
                            })}
                        </button>
                    </div>
                    <div className="-center">
                        <span className="-pageInfo">
                            {intl.formatMessage({
                                id: 'table.footer.page_text',
                            })}{' '}
                            <div className="-pageJump">
                                <input
                                    type="number"
                                    value={page}
                                    onChange={this.handlePageInput}
                                    min={1}
                                    max={totalPages}
                                />
                            </div>{' '}
                            {intl.formatMessage({
                                id: 'table.footer.of_text',
                            })}{' '}
                            <span className="-totalPages">{totalPages}</span>
                        </span>
                        <span className="-pageSizeOptions">
                            <select
                                value={pageSize}
                                onChange={this.handlePageSizeChange}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>
                                        {size}{' '}
                                        {intl.formatMessage({
                                            id: 'table.footer.rows_text',
                                        })}
                                    </option>
                                ))}
                            </select>
                        </span>
                    </div>
                    <div className="-next">
                        <button
                            type="button"
                            className="-btn"
                            disabled={page >= totalPages}
                            onClick={() => this.goToPage(page + 1)}
                        >
                            {intl.formatMessage({
                                id: 'table.footer.next_text',
                            })}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const {
            data,
            isLoading,
            showProductPhoto,
            onTogglePhoto,
            addProduct,
            setFairProducts,
            loadingState,
            downloadPdf,
            selectedProductType,
            weekAbbreviation,
            intl,
        } = this.props;
        const { pageSize, activeWeekDate } = this.state;
        const weeks = getWeeksFromData(data);
        const abbreviation =
            weekAbbreviation ||
            intl.formatMessage({ id: 'fair.week_abbreviation' });

        const pdfButtonLabelId =
            selectedProductType === 'client'
                ? 'fair.pdf_client_products'
                : selectedProductType === 'cart'
                  ? 'fair.pdf_cart_products'
                  : selectedProductType === 'promotions'
                    ? 'fair.pdf_promotions_products'
                    : 'fair.pdf_all_products';

        return (
            <div
                className={`fair-products-list ${showProductPhoto ? 'fair-products-list--with-photo' : ''
                    }`}
                ref={this.listRef}
            >

                <div className="cart__price-description">
                    <FormattedMessage id="fair.auto_save_hint" />
                </div>
                <div className="fair-products-list__weeks-header">
                    <div className="fair-products-list__weeks-header-main">
                        {showProductPhoto && (
                            <div
                                className="fair-products-list__weeks-header-photo"
                                aria-hidden="true"
                            />
                        )}

                        <div className="fair-products-list__weeks-header-content">
                            <div
                                className="fair-products-list__weeks-header-grid"
                                style={{
                                    gridTemplateColumns: `repeat(${Math.max(
                                        weeks.length,
                                        1
                                    )}, minmax(40px, 1fr))`,
                                }}
                            >
                                {weeks.map((week) => {
                                    const weekNumber = String(
                                        week.week_name
                                    ).replace(/\D/g, '');
                                    const isActive =
                                        activeWeekDate === week.week_date;

                                    return (
                                        <span
                                            key={week.week_date}
                                            className={`fair-products-list__week-tab ${isActive
                                                    ? 'fair-products-list__week-tab--active'
                                                    : ''
                                                }`}
                                        >
                                            {abbreviation}
                                            {weekNumber}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="fair-products-list__weeks-header-action fair-products-list__weeks-header-action--desktop">
                        <ButtonWithSpinner
                            label={pdfButtonLabelId}
                            isLoading={false}
                            className="btn-flat btn-red fair-pdf-button"
                            onClick={downloadPdf}
                        />
                    </div>
                </div>

                <div className="fair-products-list__photo-toggle">
                    <Input
                        onClick={onTogglePhoto}
                        name="productPhoto"
                        checked={showProductPhoto}
                        label={
                            <FormattedMessage id="table.preferences.product_photo" />
                        }
                        filled
                        type="checkbox"
                        id="fair-product-photo-toggle"
                        className="my-0"
                    />
                    <div className="fair-products-list__weeks-header-action fair-products-list__weeks-header-action--mobile">
                        <ButtonWithSpinner
                            label={pdfButtonLabelId}
                            isLoading={false}
                            className="btn-flat btn-red fair-pdf-button"
                            onClick={downloadPdf}
                        />
                    </div>
                </div>

                <div className="fair-products-list__items">
                    {isLoading && (
                        <div className="fair-products-list__loading">
                            <Spinner
                                width={40}
                                height={40}
                                className="brand-color"
                            />
                        </div>
                    )}
                    {!isLoading && (!data || data.length === 0) && (
                        <div className="fair-products-list__empty">
                            <FormattedMessage id="table.no_data_text" />
                        </div>
                    )}
                    {!isLoading &&
                        data &&
                        data.map((productItem, index) => (
                            <FairProductCard
                                key={
                                    productItem.product
                                        ? productItem.product.id
                                        : index
                                }
                                productItem={productItem}
                                index={index}
                                weeks={weeks}
                                showProductPhoto={showProductPhoto}
                                addProduct={addProduct}
                                setFairProducts={setFairProducts}
                                loadingState={loadingState}
                                pageSize={pageSize}
                                onWeekFocus={this.handleWeekFocus}
                                onWeekBlur={this.handleWeekBlur}
                            />
                        ))}
                </div>

                {this.renderPagination()}
            </div>
        );
    }
}

FairProductsList.propTypes = {
    data: PropTypes.array,
    itemsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isLoading: PropTypes.bool,
    showProductPhoto: PropTypes.bool,
    onTogglePhoto: PropTypes.func,
    onFetchData: PropTypes.func.isRequired,
    addProduct: PropTypes.func,
    setFairProducts: PropTypes.func,
    loadingState: PropTypes.object,
    downloadPdf: PropTypes.func,
    weekAbbreviation: PropTypes.string,
    selectedProductType: PropTypes.string,
    pageSize: PropTypes.number,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(FairProductsList);
